"use client";
import { useEffect, useRef } from "react";
import { Z } from "@/lib/z";

/**
 * Renders a fixed horizontal scrollbar at the bottom of the viewport that
 * mirrors the first visible overflowing `.overflow-x-auto` element whose
 * native scrollbar is below the fold. Mounts once in AppShell.
 */
export function StickyHScrollbar() {
  const barRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const activeRef = useRef<HTMLElement | null>(null);
  const syncing = useRef(false);
  const rafId = useRef(0);

  useEffect(() => {
    const bar = barRef.current;
    const inner = innerRef.current;
    if (!bar || !inner) return;

    function findActive(): HTMLElement | null {
      const els = document.querySelectorAll<HTMLElement>(".overflow-x-auto");
      for (const el of els) {
        if (el === bar) continue;
        if (el.scrollWidth <= el.clientWidth + 2) continue; // not overflowing
        const rect = el.getBoundingClientRect();
        if (rect.top >= window.innerHeight || rect.bottom <= 0) continue; // not visible
        if (rect.bottom > window.innerHeight) return el; // native scrollbar is below viewport
      }
      return null;
    }

    function onElementScroll() {
      if (syncing.current || !activeRef.current || !bar) return;
      syncing.current = true;
      bar.scrollLeft = activeRef.current.scrollLeft;
      syncing.current = false;
    }

    function onBarScroll() {
      if (syncing.current || !activeRef.current || !bar) return;
      syncing.current = true;
      activeRef.current.scrollLeft = bar.scrollLeft;
      syncing.current = false;
    }

    function updateBar() {
      const newActive = findActive();

      if (newActive !== activeRef.current) {
        activeRef.current?.removeEventListener("scroll", onElementScroll);
        activeRef.current = newActive;
        newActive?.addEventListener("scroll", onElementScroll, { passive: true });
      }

      if (!newActive || !bar || !inner) {
        if (bar) bar.style.display = "none";
        return;
      }

      const rect = newActive.getBoundingClientRect();
      bar.style.display = "block";
      bar.style.left = `${rect.left}px`;
      bar.style.width = `${rect.width}px`;
      inner.style.width = `${newActive.scrollWidth}px`;

      if (!syncing.current) {
        syncing.current = true;
        bar.scrollLeft = newActive.scrollLeft;
        syncing.current = false;
      }
    }

    function scheduleUpdate() {
      cancelAnimationFrame(rafId.current);
      rafId.current = requestAnimationFrame(updateBar);
    }

    bar.addEventListener("scroll", onBarScroll, { passive: true });
    window.addEventListener("scroll", scheduleUpdate, { passive: true });
    window.addEventListener("resize", scheduleUpdate, { passive: true });

    // Catch layout shifts: new content, navigation, resizes
    const ro = new ResizeObserver(scheduleUpdate);
    ro.observe(document.documentElement);

    updateBar();

    return () => {
      cancelAnimationFrame(rafId.current);
      bar.removeEventListener("scroll", onBarScroll);
      window.removeEventListener("scroll", scheduleUpdate);
      window.removeEventListener("resize", scheduleUpdate);
      ro.disconnect();
      activeRef.current?.removeEventListener("scroll", onElementScroll);
    };
  }, []);

  return (
    <div
      ref={barRef}
      style={{ position: "fixed", bottom: 0, height: 10, zIndex: Z.SCROLLBAR, display: "none", overflowX: "auto", overflowY: "hidden" }}
    >
      <div ref={innerRef} style={{ height: 1 }} />
    </div>
  );
}
